import React, { useEffect, useRef } from 'react';
import { useTranslation, Trans } from 'next-i18next';

import gridStyles from 'src/shared/theme/grid.module.scss';
import useRouter from 'src/shared/hooks/useRouter';
import MetaData from 'src/config/MetaData';
import RoutePaths from 'src/config/RoutePaths';
import GaService from 'src/services/GaService';
import LayoutWrapper from 'src/shared/components/LayoutWrapper';
import MarketingSection from 'src/components/MarketingSection';
import RoundedButton from 'src/shared/components/RoundedButton';
import Image from 'src/shared/components/Image';
import useScrolling from 'src/shared/hooks/useScrolling';
import ContentLink from 'src/shared/components/ContentLink';
import { fireGA } from 'src/utils/gaUtils';

import FeatureColumn from './components/FeatureColumn';
import IndustryColumn from './components/IndustryColumn';
import TraditionalVsAlternative from './components/AltiveComparisonTableSection';
import LeadershipSection from './components/LeadershipSection';
import styles from './styles.module.scss';

const pageInfo = MetaData[RoutePaths.WHY_ALTIVE];

const WhyAltive = () => {
    const { t } = useTranslation();
    const router = useRouter();
    const comparisonSectionRef = useRef<any>();
    const { scrollToView } = useScrolling();

    const clickViewComparison = () => {
        fireGA('marketingQ421', 'clickOurAdvantages', 'WA_hero');
        scrollToView(comparisonSectionRef);
    };

    const navigateToHowItWorks = () => {
        fireGA('marketingQ421', 'clickHowItWorks', 'WA_Feeder visual');
        router.pushPath(RoutePaths.HOW_IT_WORKS);
    };

    useEffect(() => {
        GaService.pageView(router.route);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [piPrefix, piPostfix] = t('why_altive_footnote', { pi: 'pi_placeholder' }).split('pi_placeholder');

    return (
        <LayoutWrapper
            className={styles.container}
            headerProps={{ type: 'white', className: styles.header, fixed: true }}
            metaProps={{ title: pageInfo.title, description: pageInfo.description, keywords: pageInfo.keywords }}
        >
            <div className={styles.page}>
                <MarketingSection containerClassName={styles.whySection} className={styles.inner} isLarge>
                    <div className={gridStyles.container}>
                        <div className={`${gridStyles.lg6} ${styles.contentColumn}`}>
                            <p className={styles.title}>{t('why_altive_title')}</p>
                            <p className={styles.content}>{t('why_altive_description')}</p>
                            <RoundedButton type="white" className={styles.viewButton} onClick={clickViewComparison}>
                                {t('why_altive_view_button')}
                            </RoundedButton>
                            <p className={styles.comment}>
                                {piPrefix}
                                <ContentLink
                                    href={RoutePaths.PROFESSIONAL_INVESTOR_NOTICE}
                                    ga={{ category: 'marketingQ421', action: 'clickPI', label: 'WA_hero' }}
                                >
                                    {t('home_professional_investor')}
                                </ContentLink>
                                {piPostfix}
                            </p>
                        </div>
                        <div className={gridStyles.lg6}>
                            <p className={styles.comment2}>
                                {piPrefix}
                                <ContentLink href={RoutePaths.PROFESSIONAL_INVESTOR_NOTICE}>{t('home_professional_investor')}</ContentLink>
                                {piPostfix}
                            </p>
                            <div className={styles.image}>
                                <Image
                                    multiLang
                                    className={styles.architectureImage}
                                    animation="fade"
                                    src="/static/images/altive_portal_mockup.png"
                                />
                            </div>
                        </div>
                    </div>
                </MarketingSection>
                <MarketingSection containerClassName={styles.weAreSection}>
                    <p className={styles.title}>{t('why_atlive_we_are_title')}</p>
                    <div className={gridStyles.container}>
                        <div className={gridStyles.lg6}>
                            <FeatureColumn icon="icon-ico_entry" title={t('accessible')} description={t('why_atlive_accessible_description')} />
                        </div>
                        <div className={gridStyles.lg6}>
                            <FeatureColumn
                                icon="icon-ico_reason1"
                                title={t('comprehensive')}
                                description={t('why_altive_comprehensive_description')}
                            />
                        </div>
                        <div className={gridStyles.lg6}>
                            <FeatureColumn
                                icon="icon-ico_platforms"
                                title={t('advanced_technology')}
                                description={t('why_altive_advanced_technology_description')}
                            />
                        </div>
                        <div className={gridStyles.lg6}>
                            <FeatureColumn
                                isLast
                                icon="icon-ico_chart"
                                title={t('diversification')}
                                description={t('why_altive_diversification_description')}
                            />
                        </div>
                    </div>
                    <p className={styles.title}>{t('why_atlive_work_with_title')}</p>
                    <div className={gridStyles.container}>
                        <div className={`${gridStyles.xl6} ${gridStyles.lg6}`}>
                            <IndustryColumn company="DBS" description={t('why_altive_dbs_description')} />
                        </div>
                        <div className={`${gridStyles.xl6} ${gridStyles.lg6}`}>
                            <IndustryColumn company="AWS" description={t('why_altive_aws_description')} />
                        </div>
                    </div>
                </MarketingSection>

                <div ref={comparisonSectionRef}>
                    <TraditionalVsAlternative />
                </div>

                <MarketingSection containerClassName={styles.technologySection} isLarge>
                    <div className={`${gridStyles.container} ${styles.content}`}>
                        <div className={gridStyles.lg6}>
                            <p className={styles.title}>{t('why_altive_technology_title')}</p>
                            <p className={styles.description}>
                                <Trans
                                    i18nKey="why_altive_technology_description"
                                    components={[
                                        <ContentLink
                                            key={0}
                                            href={RoutePaths.PROFESSIONAL_INVESTOR_NOTICE}
                                            ga={{ category: 'marketingQ421', action: 'clickPI', label: 'WA_Feeder visual' }}
                                        />,
                                    ]}
                                />
                            </p>
                            <RoundedButton type="white" className={styles.viewButton} onClick={navigateToHowItWorks}>
                                {t('menu_how_it_works')}
                            </RoundedButton>
                        </div>
                        <div className={`${gridStyles.lg6} ${styles.imageColumn}`}>
                            <Image
                                multiLang
                                className={styles.architectureImage}
                                animation="fade"
                                src="/static/images/img_feeder_fund_intro@2x.jpg"
                            />
                        </div>
                    </div>
                </MarketingSection>

                <LeadershipSection />
            </div>
        </LayoutWrapper>
    );
};

export default WhyAltive;
